import { createTheme, Theme } from "@fluentui/react";
import { fluentPalettes } from "./palette";

// Fluent v8 theme objects consumed by <ThemeProvider theme={...}>.
//
// v8 ships no dark theme (webDarkTheme lives only in v9 / react-components),
// so we build one from our palette. createTheme derives sensible
// semanticColors from the palette; the explicit overrides below fix the few
// slots that don't invert cleanly on dark surfaces.

export const lightTheme: Theme = createTheme({
  palette: fluentPalettes.light,
});

export const darkTheme: Theme = createTheme({
  // Tell Fluent this ramp is inverted. Without it, createTheme leaves every
  // MessageBar semantic slot at its LIGHT default (warningBackground #FFF4CE,
  // messageText #323130, warningIcon #797775, …) because those slots are gated
  // on `isInverted` and are NOT re-derived from the palette by mergeThemes — so
  // warning/error/success/info bars render as bright light blocks on the dark
  // UI. Setting it flips exactly the 18 MessageBar-family slots to Fluent's dark
  // values; every other slot is unaffected and the explicit overrides below
  // still win.
  isInverted: true,
  palette: fluentPalettes.dark,
  semanticColors: {
    // Base surfaces / text.
    bodyBackground: "#1f1f1f",
    bodyText: "#f3f2f1",
    // Raised surfaces (cards, panels, menus) sit LIGHTER than the body in
    // dark mode — elevation is expressed with lightness, not shadow.
    cardStandoutBackground: "#2b2b2b",
    menuBackground: "#2b2b2b",
    listBackground: "#1f1f1f",
    // Inputs need a visible-but-subtle fill against the dark body.
    inputBackground: "#1f1f1f",
    inputBorder: "#5f5f5f",
    // Dividers.
    bodyDivider: "#3f3f3f",
    // Desaturated error/link so they don't glare on dark.
    errorText: "#f1707b",
    link: "#479ef5",
    linkHovered: "#6bb2f7",
  },
  components: {
    // Persona initials render in palette.white, which this ramp inverts to
    // #1f1f1f — near-black initials on the fixed saturated coin colors are
    // unreadable. The coin palette is identical in both themes, so pin the
    // initials to literal white, matching light mode's contrast.
    PersonaCoin: {
      styles: {
        initials: { color: "#ffffff" },
      },
    },
  },
});

export const themeFor = (name: "light" | "dark"): Theme =>
  name === "dark" ? darkTheme : lightTheme;
